import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "clinician-panel w-full col-12 mt-3 p-3 border-round-md bg-gray-100" }
const _hoisted_2 = { class: "inline-flex w-full pt-0 pb-3" }
const _hoisted_3 = { class: "flex flex-wrap align-content-center mr-2" }
const _hoisted_4 = ["textContent"]
const _hoisted_5 = { class: "disciplines inline-flex align-content-center flex-wrap" }
const _hoisted_6 = { class: "flex flex-column flex-wrap align-content-center justify-content-end ml-auto" }
const _hoisted_7 = { class: "pb-2 flex justify-content-end" }
const _hoisted_8 = { class: "pl-1" }
const _hoisted_9 = { class: "pb-2 flex justify-content-end" }
const _hoisted_10 = { class: "pl-1" }
const _hoisted_11 = { class: "flex justify-content-end" }
const _hoisted_12 = { class: "pl-1" }
const _hoisted_13 = { class: "inline-flex w-full pt-0" }
const _hoisted_14 = { class: "col p-3 border-round-left-md bg-white border-right-1 border-gray-100 bg-white" }
const _hoisted_15 = { class: "field" }
const _hoisted_16 = { class: "col p-3 border-round-right-md bg-white border-left-1 border-gray-100 bg-white" }
const _hoisted_17 = { class: "field mb-0" }
const _hoisted_18 = { class: "flex mb-0" }
const _hoisted_19 = { class: "col p-0 field-radiobutton" }
const _hoisted_20 = { class: "col p-0 inline-flex" }
const _hoisted_21 = { class: "flex" }
const _hoisted_22 = { class: "col p-0 field-radiobutton mb-0" }
const _hoisted_23 = { class: "col p-0 inline-flex" }

import { computed, toRefs, watch, ref, onMounted } from 'vue';
import InputNumber from 'primevue/inputnumber';
import RadioButton from 'primevue/radiobutton';
import ClinicianDisciplineIcon from './ClinicianDisciplineIcon.vue';
import OrganizationContractRemoveServiceProviderModal from './OrganizationContractRemoveServiceProviderModal.vue';
import { ClinicianContractDetails } from '@/types';
import { CONTRACT_ENUMS } from '@/enum';
import { useDateTime } from '@/composables';
import { useOrganizationContractsStore } from '@/store';

interface Props {
  clinician: ClinicianContractDetails;
  justAdded?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'OrganizationContractClinicianPanel',
  props: {
    clinician: {},
    justAdded: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;
const { clinician, justAdded } = toRefs(props);
const contractStore = useOrganizationContractsStore();
const loading = ref(true);
const removeServiceProviderModalOpen = ref(false);
const canEdit = contractStore.canEditOrCreateContracts;
const editType = contractStore.editType;
const hasNoEventsScheduled = ref(false);

onMounted(async () => {
  if (justAdded.value || editType !== CONTRACT_ENUMS.CONTRACT_EDIT_TYPES.EDIT) {
    loading.value = false;
    return;
  }
  await checkClinicianEventCount();
});

const checkClinicianEventCount = async () => {
  const response = await contractStore
    .getServiceProviderEventCount(clinician.value.id as number)
    .catch(() => (loading.value = false));
  if (response === 0) hasNoEventsScheduled.value = true;
  loading.value = false;
};

const hourlyRate = computed<number>({
  get() {
    return !clinician.value.rate_is_hourly ? 0 : clinician.value.rate;
  },
  set(value) {
    clinician.value.rate = value;
  },
});

const salaryRate = computed<number>({
  get() {
    return clinician.value.rate_is_hourly ? 0 : clinician.value.rate;
  },
  set(value) {
    clinician.value.rate = value;
  },
});

const removedFromContractDate = computed<string>(() => {
  if (clinician.value.document_by_date) {
    const documentByDate = new Date(clinician.value.document_by_date);
    const followingMonth = ((documentByDate.getMonth() + 1) % 12) + 1;
    const yearValue = followingMonth !== 1 ? documentByDate.getFullYear() : documentByDate.getFullYear() + 1;

    return `${followingMonth}/01/${yearValue}`;
  }
  return '';
});

const limitToCreateEvents = computed<string>(() => {
  if (clinician.value.schedule_by_date) {
    const scheduleByDate = new Date(clinician.value.schedule_by_date);
    const month = scheduleByDate.getMonth() + 1;
    const day = scheduleByDate.getDate();
    const year = scheduleByDate.getFullYear();

    return `${month}/${day}/${year}`;
  }
  return '';
});

const documentByDate = computed<string>(() => {
  if (clinician.value.document_by_date) {
    const documentByDate = new Date(clinician.value.document_by_date);

    const month = documentByDate.getMonth() + 1;
    const day = documentByDate.getDate();
    const year = documentByDate.getFullYear();

    return `${month}/${day}/${year}`;
  }
  return '';
});

//KEY for discipline code/icon name
const icons = [
  { discipline_name: 'Behavioral Intervention', code: 'BI', name: 'emoji_objects' },
  { discipline_name: 'Occupational Therapy', code: 'OT', name: 'work' },
  { discipline_name: 'Physical Therapy', code: 'PT', name: 'emoji_people' },
  { discipline_name: 'Social Work/Counseling', code: 'SWC', name: 'emoji_people' },
  { discipline_name: 'Psychology', code: 'PSY', name: 'psychology' },
  { discipline_name: 'Speech And Language', code: 'SL', name: 'record_voice_over' },
];

/**
 * find the icon that goes with the discipline
 */
const getObjectByCodeOrName = (code: string) =>
  icons.find((icon) => icon.code === code || icon.discipline_name === code);

watch(
  clinician,
  () => {
    clinician.value.updated = true;
    clinician.value.person_id = clinician.value.person?.id;
  },
  { deep: true },
);

/**
 * delete the clinician
 */
const onRemove = async (endDates: Record<string, Date>) => {
  toggleConfirmRemoveServiceProviderModal();
  const formattedEndDates = {
    schedule_by_date: useDateTime().formatDateForApi(endDates.endDate),
    document_by_date: useDateTime().formatDateForApi(endDates.serviceEndDate),
  };
  await contractStore.deleteServiceProvider(clinician.value.id as number, formattedEndDates, false);
  clinician.value.document_by_date = formattedEndDates.document_by_date;
  clinician.value.schedule_by_date = formattedEndDates.schedule_by_date;
  contractStore.doToast('Service Provider has been removed', 'Removed', false);
};

const toggleConfirmRemoveServiceProviderModal = async () => {
  const preventDateSelectionModal =
    justAdded.value || !contractStore.selectedContractStarted || hasNoEventsScheduled.value;
  if (preventDateSelectionModal) {
    // if the clinician being removed was only temporarily on the contract before it was saved
    // we follow this flow to prevent the confirm modal from showing
    await contractStore.deleteServiceProvider(
      clinician.value.id as number,
      { schedule_by_date: null, document_by_date: null },
      true,
    );
    contractStore.doToast('Service Provider has been removed', 'Removed', false);
    return;
  }

  removeServiceProviderModalOpen.value = !removeServiceProviderModalOpen.value;
};

const undoServiceProviderRemoval = async () => {
  await contractStore.restoreServiceProvider(clinician.value.id as number);
  clinician.value.document_by_date = null;
  clinician.value.schedule_by_date = null;
  contractStore.doToast('Service Provider will no longer be removed from this contract after save', 'Success', false);
};

return (_ctx: any,_cache: any) => {
  const _component_Icon = _resolveComponent("Icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("span", {
          class: "text-sm font-semibold",
          textContent: _toDisplayString(_unref(clinician).person?.display_name)
        }, null, 8, _hoisted_4)
      ]),
      _createElementVNode("div", _hoisted_5, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(clinician).person?.clinician_disciplines, (discipline, key) => {
          return (_openBlock(), _createBlock(ClinicianDisciplineIcon, {
            key: key,
            icon: getObjectByCodeOrName(discipline.code || discipline.name),
            "class-name": "inline-flex px-2"
          }, null, 8, ["icon"]))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_6, [
        (!_unref(clinician).schedule_by_date && !_unref(clinician).document_by_date && _unref(canEdit) && !loading.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "p-link justify-content-end",
              onClick: toggleConfirmRemoveServiceProviderModal
            }, [
              _createVNode(_component_Icon, {
                name: "delete",
                size: "text-xl",
                class: "text-600"
              })
            ]))
          : (_unref(canEdit) && !loading.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                _createElementVNode("span", _hoisted_7, [
                  _cache[7] || (_cache[7] = _createTextVNode(" Will no longer appear on this contract after ", -1)),
                  _createElementVNode("b", _hoisted_8, _toDisplayString(removedFromContractDate.value), 1)
                ]),
                _createElementVNode("span", _hoisted_9, [
                  _cache[8] || (_cache[8] = _createTextVNode(" Cannot create events after ", -1)),
                  _createElementVNode("b", _hoisted_10, _toDisplayString(limitToCreateEvents.value), 1)
                ]),
                _createElementVNode("span", _hoisted_11, [
                  _cache[9] || (_cache[9] = _createTextVNode(" Cannot edit or document events after ", -1)),
                  _createElementVNode("b", _hoisted_12, _toDisplayString(documentByDate.value), 1)
                ]),
                _createElementVNode("button", {
                  class: "p-link flex ml-auto pt-2 align-items-center",
                  onClick: undoServiceProviderRemoval
                }, [
                  _createVNode(_component_Icon, {
                    name: "undo",
                    size: "text-xl",
                    class: "text-primary mt-1"
                  }),
                  _cache[10] || (_cache[10] = _createElementVNode("span", { class: "p-button-label text-primary ml-1" }, [
                    _createElementVNode("b", null, "Undo Remove")
                  ], -1))
                ])
              ], 64))
            : _createCommentVNode("", true)
      ])
    ]),
    _createElementVNode("div", _hoisted_13, [
      _createElementVNode("div", _hoisted_14, [
        _createElementVNode("div", _hoisted_15, [
          _cache[11] || (_cache[11] = _createElementVNode("label", { for: "hours_contracted" }, "Hours Contracted", -1)),
          _createVNode(_unref(InputNumber), {
            id: "hours_contracted",
            modelValue: _unref(clinician).hours,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(clinician).hours) = $event)),
            class: "data w-full",
            disabled: !_unref(canEdit),
            mode: "decimal",
            "max-fraction-digits": 2,
            "min-fraction-digits": 0
          }, null, 8, ["modelValue", "disabled"])
        ])
      ]),
      _createElementVNode("div", _hoisted_16, [
        _createElementVNode("div", _hoisted_17, [
          _cache[14] || (_cache[14] = _createElementVNode("label", { for: "no_show" }, "Compensation Type", -1)),
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("div", _hoisted_19, [
              _createVNode(_unref(RadioButton), {
                modelValue: _unref(clinician).rate_is_hourly,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(clinician).rate_is_hourly) = $event)),
                value: 1,
                "input-id": "hourly",
                name: "payment_type",
                "input-class": "mb-0",
                class: "relative",
                disabled: !_unref(canEdit)
              }, null, 8, ["modelValue", "disabled"]),
              _cache[12] || (_cache[12] = _createElementVNode("label", { for: "hourly" }, "Hourly", -1))
            ]),
            _createElementVNode("div", _hoisted_20, [
              _createVNode(_unref(InputNumber), {
                modelValue: hourlyRate.value,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((hourlyRate).value = $event)),
                placeholder: "$0",
                mode: "currency",
                currency: "USD",
                locale: "en-US",
                class: "p-inputtext-sm ml-auto",
                "input-style": { height: '1.5rem', width: '7.25rem' },
                disabled: _unref(clinician).rate_is_hourly === 0 || !_unref(canEdit),
                onFocus: _cache[3] || (_cache[3] = (e) => e?.target?.select())
              }, null, 8, ["modelValue", "disabled"])
            ])
          ]),
          _createElementVNode("div", _hoisted_21, [
            _createElementVNode("div", _hoisted_22, [
              _createVNode(_unref(RadioButton), {
                modelValue: _unref(clinician).rate_is_hourly,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(clinician).rate_is_hourly) = $event)),
                value: 0,
                "input-id": "salary",
                name: "payment_type",
                "input-class": "mb-0",
                class: "relative",
                disabled: !_unref(canEdit)
              }, null, 8, ["modelValue", "disabled"]),
              _cache[13] || (_cache[13] = _createElementVNode("label", { for: "salary" }, "Salary", -1))
            ]),
            _createElementVNode("div", _hoisted_23, [
              _createVNode(_unref(InputNumber), {
                modelValue: salaryRate.value,
                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((salaryRate).value = $event)),
                disabled: _unref(clinician).rate_is_hourly === 1 || !_unref(canEdit),
                placeholder: "$0",
                mode: "currency",
                currency: "USD",
                locale: "en-US",
                class: "p-inputtext-sm ml-auto",
                min: 0,
                "input-style": { height: '1.5rem', width: '7.25rem' },
                onFocus: _cache[6] || (_cache[6] = (e) => e?.target?.select())
              }, null, 8, ["modelValue", "disabled"])
            ])
          ])
        ])
      ])
    ]),
    (removeServiceProviderModalOpen.value)
      ? (_openBlock(), _createBlock(OrganizationContractRemoveServiceProviderModal, {
          key: 0,
          clinician: _unref(clinician),
          onRemove: onRemove,
          onRemoveCancel: toggleConfirmRemoveServiceProviderModal
        }, null, 8, ["clinician"]))
      : _createCommentVNode("", true)
  ]))
}
}

})